/*
 * The colour of an ad.
 *
 * Each ad wears one colour: the big block with the icon on the right of its card
 * and the bottom border tied to it. The owner picks from this fixed palette
 * rather than typing a hex, for two reasons: every choice is guaranteed to read
 * with white on top of it, and the server can refuse anything outside it, so a
 * stored value is never markup or an unreadable pastel. A logo will replace the
 * icon later; the colour stays.
 */
export const SERVICE_COLORS = {
  teal: '#0E6B72',
  coral: '#D8434A',
  amber: '#B86E00',
  indigo: '#4B50B8',
  green: '#2E7D4F',
  plum: '#8A3F8F',
  sky: '#1F7AB8',
  slate: '#455468',
} as const

export type ServiceColorId = keyof typeof SERVICE_COLORS
export const SERVICE_COLOR_IDS = Object.keys(SERVICE_COLORS) as ServiceColorId[]

export function isServiceColor(value: unknown): value is ServiceColorId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SERVICE_COLORS, value)
}

/** What an ad with no chosen colour wears: teal, the brand. */
export function serviceColorHex(value: string | null | undefined): string {
  return isServiceColor(value) ? SERVICE_COLORS[value] : SERVICE_COLORS.teal
}

/**
 * A steady colour for something that has none chosen (a business on a job card):
 * the same seed always gives the same colour, so a business does not change
 * colour from one visit to the next.
 */
export function colorIdFor(seed: string): ServiceColorId {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return SERVICE_COLOR_IDS[h % SERVICE_COLOR_IDS.length]!
}
